import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import { Agent } from "@/types/agent/agent";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import GenieShareDrawer from "@/components/agent/drawers/GenieShareDrawer";

interface WelcomeMessagesCardProps {
  agent: Agent;
  brand?: string;
}

export const WelcomeMessagesCard = ({ agent, brand = "#4E9CFF" }: WelcomeMessagesCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [voiceMessage, setVoiceMessage] = useState(
    agent.settings?.conversation_config?.agent?.first_message ?? "",
  );
  const [webMessage, setWebMessage] = useState(
    agent.welcome_message ?? agent.page?.welcome_message ?? "",
  );
  const [urlName, setUrlName] = useState(agent.url_name ?? agent.page?.url_name ?? "");
  const [copiedUrl, setCopiedUrl] = useState(false);

  const updateAgent = useUpdateAgent();

  const publicBaseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const currentUrlName = isEditing ? urlName : (agent.url_name ?? agent.page?.url_name ?? "");
  const publicUrl = currentUrlName ? `${publicBaseUrl}/live-agent/${currentUrlName}` : null;

  const handleEdit = () => {
    setVoiceMessage(agent.settings?.conversation_config?.agent?.first_message ?? "");
    setWebMessage(agent.welcome_message ?? agent.page?.welcome_message ?? "");
    setUrlName(agent.url_name ?? agent.page?.url_name ?? "");
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    await updateAgent.mutateAsync({
      agent,
      updateData: {
        settings: {
          ...agent.settings,
          conversation_config: {
            ...agent.settings?.conversation_config,
            agent: {
              ...agent.settings?.conversation_config?.agent,
              first_message: voiceMessage,
            },
          },
        },
        page: {
          ...agent.page,
          welcome_message: webMessage,
          url_name: urlName,
        },
      },
    });
    toast.success("Welcome messages saved");
    setIsEditing(false);
  };

  const handleCopyUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopiedUrl(true);
      toast.success("URL copied to clipboard");
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch {
      toast.error("Failed to copy URL");
    }
  };

  return (
    <OverviewSectionCard
      title="Welcome Messages"
      subtitle="First impressions for audio and web"
      genieIcon="chat-heart"
      brand={brand}
      secondaryColor={agent.branding?.secondaryColor}
      gradientEnabled={agent.branding?.gradientEnabled}
      gradientAngle={agent.branding?.gradientAngle}
      onEdit={isEditing ? undefined : handleEdit}
      onSave={isEditing ? handleSave : undefined}
      onCancel={isEditing ? handleCancel : undefined}
      isSaving={updateAgent.isPending}
    >
      {isEditing ? (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Welcome Message (Audio/Voice)
            </label>
            <textarea
              value={voiceMessage}
              onChange={(e) => setVoiceMessage(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 h-24 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Enter the welcome message users hear when they first interact with the genie..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Spoken by the voice agent when users start a conversation
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Welcome Message (Web Page)
            </label>
            <textarea
              value={webMessage}
              onChange={(e) => setWebMessage(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 h-24 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Enter the welcome message shown on the web page..."
            />
            <p className="text-xs text-gray-500 mt-1">Displayed on the agent's web interface</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Genie Page URL</label>

            {publicUrl && (
              <div className="mb-3 flex items-center gap-2">
                <span className="text-xs bg-gray-100 px-2 py-1 rounded border border-gray-200 flex-1 truncate text-gray-700">
                  {publicUrl}
                </span>
                <button
                  onClick={handleCopyUrl}
                  className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                  title="Copy link"
                >
                  {copiedUrl ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-600" />
                  )}
                </button>
              </div>
            )}

            <label className="block text-xs font-medium text-gray-600 mb-1">Edit URL slug</label>
            <input
              type="text"
              value={urlName}
              onChange={(e) => setUrlName(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="my-genie-url"
            />
            <p className="text-xs text-gray-500 mt-2">This becomes your agent's public page URL</p>
          </div>

        </div>
      ) : (
        <div className="space-y-6">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">Voice Welcome Message</div>
            <p className="text-sm text-gray-600 mb-3">
              Spoken by the voice agent when users start a conversation
            </p>
            {agent.settings?.conversation_config?.agent?.first_message ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm italic text-gray-900">
                "{agent.settings.conversation_config.agent.first_message}"
              </div>
            ) : (
              <span className="text-gray-400 text-sm italic">Not set</span>
            )}
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">Web Page Message</div>
            <p className="text-sm text-gray-600 mb-3">
              Displayed on the agent's web interface
            </p>
            {agent.welcome_message ?? agent.page?.welcome_message ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm italic text-gray-900">
                "{agent.welcome_message ?? agent.page?.welcome_message}"
              </div>
            ) : (
              <span className="text-gray-400 text-sm italic">Not set</span>
            )}
          </div>

          {publicUrl && (
            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">Page URL</div>
              <p className="text-sm text-gray-600 mb-3">Your agent's public page address</p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center gap-2">
                <span className="text-sm text-gray-900 flex-1 truncate">{publicUrl}</span>
                <button
                  onClick={handleCopyUrl}
                  className="p-1 hover:bg-gray-200 rounded transition-colors shrink-0"
                  title="Copy link"
                >
                  {copiedUrl ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-600" />
                  )}
                </button>
              </div>
              {!agent.is_public && (
                <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 rounded-lg bg-blue-50 border border-blue-200">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-blue-900">Access Limited</p>
                    <p className="text-xs text-gray-700">Accessible only to the owner and team members with granted access.</p>
                  </div>
                  <button
                    onClick={() => setShareOpen(true)}
                    className="w-full sm:w-auto sm:shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                    style={{ backgroundColor: `${brand}15`, color: brand }}
                  >
                    Manage Access
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <GenieShareDrawer genie={agent} isOpen={shareOpen} onClose={() => setShareOpen(false)} />
    </OverviewSectionCard>
  );
};
